import React from 'react'
import { Link } from 'react-router-dom'
import { Divider, Row, Col, Form, Input, Select, Button, Skeleton, message, Typography, Radio } from 'antd'
import * as deepObject from 'deep-object-diff'
import { FormComponent } from '../../../BaseComponent'
import { PartnerKeyTypeArray } from '../../../../interfaces/IIntegration'
import IShelter from '../../../../interfaces/IShelter'
import { IntegrationConstants } from '../../../../helpers/enums/Integration'
import { SearchRequest } from '../../../../services/api/interfaces/ApiRequest'
import { PartnerAuthRequest } from '../../../../services/integration/interfaces/ApiRequest'
import { integrationCredentialList } from '../../../../services/integration'
import config from '../../../../config'

// Allow overriding the flag at runtime via the query string
// (e.g. ?flag_enable_credential_list=true), falling back to the build-time config flag.
const readCredentialListFlagOverride = (): boolean => {
  if (typeof window === 'undefined') return false

  try {
    return new URLSearchParams(window.location.search)
      .get('flag_enable_credential_list') === 'true'
  } catch (e) {
    return false
  }
}

const FLAG_ENABLE_CREDENTIAL_LIST = config.flags.FLAG_ENABLE_CREDENTIAL_LIST || readCredentialListFlagOverride()

type CredentialMethod = 'legacy' | 'existing'

type IntegrationAuthProps = {
  authUuid?: string | null,
  error: any | null

  loading: boolean,
  contentLoading?: boolean
  history: any,

  shelters: IShelter[] | null

  loadShelters: (args: SearchRequest) => Promise<any>,

  authPartner: (args: PartnerAuthRequest) => Promise<any>
}

export default class IntegrationAuth extends FormComponent<IntegrationAuthProps> {

  static defaultProps = {
    contentLoading: true
  }

  state = {
    saving: false,
    loaded: false,

    partnerKey: '',
    shelterUuid: '',

    // Credential source selection (only relevant when FLAG_ENABLE_CREDENTIAL_LIST is on)
    credentialMethod: 'legacy' as CredentialMethod,
    credentialUuid: '',
    credentials: [] as any[],
    loadingCredentials: false
  }

  componentDidMount() {
    const { shelters, loadShelters } = this.props

    if (!shelters) {
      loadShelters({
        search: {
          deleted: false,
          organization_branches: {
            with: {
              deleted: false
            }
          }
        },
        options: {
          limit: 9999,
          // Needed so shelter records carry their numeric `id`, which the
          // credential list is filtered by (shelter_id).
          _force_ids: true
        }
      })
    }
  }

  componentDidUpdate(prevProps: IntegrationAuthProps, prevState: any) {
    const wasLoading = prevProps.loading && !this.props.loading
    let newState = Object.assign({}, this.state)

    if (!this.props.contentLoading && !newState.loaded) {
      newState.loaded = true
    }

    if (this.state.loaded) {
      if (this.state.saving && wasLoading) {
        newState.saving = false

        if (this.props.error) {
          message.error(`Could not make authentication with partner. Reason ${this.props.error}`)
        }
      }
    }

    const hasChanged = Object.keys(deepObject.diff(this.state, newState)).length > 0
    if (hasChanged) {
      this.setState(newState)
    }
  }

  private useExistingCredential = () =>
    FLAG_ENABLE_CREDENTIAL_LIST && this.state.credentialMethod === 'existing'

  private handleSave = (values: any) => {
    const { authPartner } = this.props
    const { partnerKey, shelterUuid, credentialUuid } = this.state

    const credentials = IntegrationConstants.CREDENTIALS_FIELDS.find(p => p.partner_key === partnerKey)

    const payload: PartnerAuthRequest = {
      endpoint: credentials?.endpoint,
      shelter_uuid: shelterUuid,
      ...values
    }

    if (this.useExistingCredential()) {
      // Authenticate using an already stored credential instead of inline fields.
      payload.credential_uuid = credentialUuid
      delete payload.credentials
    }

    this.setState({
      saving: true
    })
    authPartner(payload)
  }

  private loadCredentialCandidates = async () => {
    const { shelters } = this.props
    const { partnerKey, shelterUuid } = this.state

    if (!partnerKey || !shelterUuid) return

    const shelterId = shelters?.find(s => s.uuid === shelterUuid)?.id
    if (!shelterId) return

    this.setState({ loadingCredentials: true })
    try {
      const response = await integrationCredentialList({
        search: {
          partner_key: partnerKey,
          shelter_id: shelterId,
          shelter: {
            with: true
          }
        },
        options: {
          limit: 100,
          sort: ['-created_date']
        }
      })

      const result = (response?.data as any)?.data?.result || []

      // Defensive client-side filter: only keep credentials that actually match
      // the selected shelter and partner, so a mismatched credential can never
      // be listed even if the API loosens the server-side filter.
      const filtered = result.filter((credential: any) => {
        const matchesShelter =
          credential?.shelter_id === shelterId ||
          credential?.shelter?.id === shelterId ||
          credential?.shelter?.uuid === shelterUuid
        const matchesPartner = !credential?.partner_key || credential.partner_key === partnerKey

        return matchesShelter && matchesPartner
      })

      this.setState({ credentials: filtered })
    } catch (e) {
      message.error('Could not load stored credentials.')
      this.setState({ credentials: [] })
    } finally {
      this.setState({ loadingCredentials: false })
    }
  }

  private handleCredentialMethodChange = (credentialMethod: CredentialMethod) => {
    this.setState({ credentialMethod, credentialUuid: '' }, () => {
      if (credentialMethod === 'existing') {
        this.loadCredentialCandidates()
      }
    })
  }

  renderPartners = () => {
    return (<div style={{ marginTop: 4 }}>
      <Divider />
      <Typography.Title level={5}>Shelter Management Software</Typography.Title>
      <Select
        placeholder="Partner"
        allowClear={true}
        options={PartnerKeyTypeArray.map(partnerKey => {
          return {
            label: partnerKey,
            value: partnerKey
          }
        })}
        style={{
          width: 200
        }}
        onChange={(partnerKey: string) => this.setState({ partnerKey, credentialUuid: '', credentials: [] }, () => {
          if (this.useExistingCredential()) this.loadCredentialCandidates()
        })}
      />
    </div>
    )
  }

  renderShelters = () => {
    const { shelters } = this.props
    const { partnerKey } = this.state
    if (!partnerKey) return null

    return (
      <div style={{ marginTop: 4 }}>
        <Divider />
        <Typography.Title level={5}>Shelter</Typography.Title>
        <Select
          placeholder="Shelter"
          allowClear={true}
          showSearch={true}
          options={shelters?.map(shelter => {
            return {
              label: shelter.name,
              value: shelter.uuid
            }
          })}
          style={{
            width: 200
          }}
          filterOption={(input, option) => {
            if (!option?.label || typeof option.label !== 'string') return false

            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }}
          onChange={(shelterUuid: string) => this.setState({ shelterUuid, credentialUuid: '', credentials: [] }, () => {
            if (this.useExistingCredential()) this.loadCredentialCandidates()
          })}
        />
      </div>
    )
  }

  renderBranches = () => {
    const { shelters } = this.props
    const { shelterUuid } = this.state
    if (!shelterUuid) return null

    const branches = shelters?.find(p => p.uuid === shelterUuid)?.organization_branches || []

    return (
      <>
        <Divider />
        <Typography.Title level={5}>Shelter Location</Typography.Title>
        <Form.Item noStyle shouldUpdate={true}>
          {({ getFieldValue }) => {
            const branchUuid = getFieldValue("branch_uuid")

            const renderHint = () => <>Without a fixed location, integration will depend on the <Link to={"/integration-mapping/branch"} target="_blank">branch mapping</Link></>

            return <Form.Item
              name="branch_uuid"
              help={!branchUuid && renderHint()}
            >
              <Select
                placeholder="Location"
                allowClear={true}
                options={branches?.map(branch => {
                  return {
                    label: branch.name,
                    value: branch.uuid
                  }
                })}
                style={{
                  width: 200
                }}
              />
            </Form.Item>
          }}
        </Form.Item>
      </>

    )
  }

  renderTypeKey = () => {
    const { shelterUuid } = this.state
    if (!shelterUuid) return null

    return (
      <>
        <Divider />
        <Typography.Title level={5}>Integration Type</Typography.Title>
        <Form.Item
          name="type_key"
          initialValue="ADOPTION"
          rules={[
            {
              required: true,
              message: 'Required field'
            }
          ]}
        >
          <Select
            placeholder="Type Key"
            options={[
              { label: 'ADOPTION', value: 'ADOPTION' },
              { label: 'LOST', value: 'LOST' },
              { label: 'FOUND', value: 'FOUND' }
            ]}
            style={{
              width: 200
            }}
          />
        </Form.Item>
      </>
    )
  }

  mountFields = () => {
    const { partnerKey } = this.state
    if (!partnerKey || partnerKey.length === 0) return null

    const credentials = IntegrationConstants.CREDENTIALS_FIELDS.find(p => p.partner_key === partnerKey)
    if (!credentials) return null

    return (
      <Row gutter={24}>
        {
          Object.keys(credentials.fields).map(key => {
            const field = credentials.fields[key]

            return (
              <Col key={`credential-field-${key}`}>
                <Form.Item
                  label={field.label}
                  name={['credentials', key]}
                  rules={[
                    {
                      required: field.required,
                      message: 'Required field'
                    }
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            )
          })
        }
      </Row>
    )
  }

  renderCredentialMethodToggle = () => {
    const { credentialMethod } = this.state

    return (
      <div style={{ marginBottom: 16 }}>
        <Typography.Title level={5}>Credentials</Typography.Title>
        <Radio.Group
          optionType="button"
          buttonStyle="solid"
          value={credentialMethod}
          onChange={(e) => this.handleCredentialMethodChange(e.target.value)}
          options={[
            { label: 'Enter manually', value: 'legacy' },
            { label: 'Use existing credential', value: 'existing' }
          ]}
        />
      </div>
    )
  }

  renderCredentialSelect = () => {
    const { credentials, credentialUuid, loadingCredentials } = this.state

    return (
      <div>
        <Select
          placeholder="Select a stored credential"
          allowClear={true}
          showSearch={true}
          loading={loadingCredentials}
          value={credentialUuid || undefined}
          notFoundContent={loadingCredentials ? 'Loading...' : 'No stored credentials for this shelter/partner'}
          options={credentials.map(credential => {
            const label = credential.description
              ? `${credential.description} (${credential.uuid})`
              : credential.uuid

            return {
              label,
              value: credential.uuid
            }
          })}
          style={{
            minWidth: 320,
            maxWidth: '100%'
          }}
          filterOption={(input, option) => {
            if (!option?.label || typeof option.label !== 'string') return false

            return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }}
          onChange={(credentialUuid: string) => this.setState({ credentialUuid: credentialUuid || '' })}
        />
      </div>
    )
  }

  renderCredentialsSection = () => {
    const { partnerKey } = this.state
    if (!partnerKey || partnerKey.length === 0) return null

    // Legacy-only when the flag is disabled.
    if (!FLAG_ENABLE_CREDENTIAL_LIST) {
      return this.mountFields()
    }

    return (
      <>
        {this.renderCredentialMethodToggle()}
        {this.useExistingCredential() ? this.renderCredentialSelect() : this.mountFields()}
      </>
    )
  }

  render() {
    const { loaded, saving, shelterUuid } = this.state

    if (!loaded) {
      return <Skeleton />
    }

    return (
      <Form
        name="integration_auth"
        layout="vertical"
        onFinish={this.handleSave}
        style={{
          marginTop: 30
        }}
      >
        <Col>
          <Col>
            <Typography.Title level={5}>Integration description</Typography.Title>
            <Typography.Text>This is the short description that will identify your integration. As an Adopets standard, you can use a combination of shelter name + shelter location. </Typography.Text>
            <Form.Item
              name="description"
              rules={[
                {
                  required: true,
                  message: 'Description is required'
                }
              ]}
            >
              <Input placeholder="Integration Description" />
            </Form.Item>
          </Col>

          <Col>
            {this.renderPartners()}
          </Col>

          <Col style={{ marginTop: 4 }}>
            {this.renderShelters()}
          </Col>

          <Col>
            {this.renderBranches()}
          </Col>

          <Col>
            {this.renderTypeKey()}
          </Col>
        </Col>

        <Divider />

        {
          this.renderCredentialsSection()
        }

        <Form.Item style={{ marginTop: 32 }}>
          <Button type="primary" htmlType="submit" size="middle" loading={saving} disabled={!shelterUuid || (this.useExistingCredential() && !this.state.credentialUuid)}>
            Continue
          </Button>
        </Form.Item>
      </Form>
    )
  }
}
